import { Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/store/authStore';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { PageSkeleton } from '@/components/shared/PageSkeleton';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { type Permission } from '@/lib/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
}

/**
 * Component to protect routes that require authentication
 * Optionally checks for specific permissions
 *
 * Waits for AuthInitializer to complete before checking authentication
 * to prevent redirects when user has valid session but store not yet populated
 *
 * Redirects to /onboarding if onboarding is required (except when already on /onboarding)
 */
export function ProtectedRoute({
  children,
  permission,
  permissions,
  requireAll = false,
}: ProtectedRouteProps) {
  const { t } = useTranslation('auth');
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const { isSetupRequired, requiresTwoFactor, requiresTwoFactorEnrollment } = useAuthStore();
  const location = useLocation();
  const isAdminShellPath =
    location.pathname.startsWith('/admin') || location.pathname.startsWith('/dashboard');

  // Show loading state while auth is being initialized or checked.
  // This prevents premature redirect to login when session is being verified.
  // #181/#676: on admin-shell routes render chrome-preserving skeleton
  // immediately instead of a bare full-page spinner — a blank/spinner-only
  // first paint made page loads feel broken.
  //
  // #676 root cause: this branch used to mount the REAL `AdminLayout`
  // (sidebar/header/CommandPalette + their data hooks) via `React.lazy`, so
  // first paint was a bare spinner with NO chrome at all while that chunk
  // downloaded — then, once auth resolved, `{children}` below (the
  // destination page, itself lazy and NOT locally wrapped in Suspense) would
  // suspend again and bubble past this component to the App-level
  // `<Suspense fallback={<PageSkeleton />}>` in App.tsx, unmounting the
  // AdminLayout tree just rendered and replacing it with a *different*
  // skeleton implementation — two chrome-shaped but structurally distinct
  // trees mounting in sequence reads as a "shell replace" flash.
  //
  // Fix: use `PageSkeleton` — a plain, eagerly-imported, hook-free component
  // (no chunk to await, so it paints on the very first render) — for BOTH
  // this loading gate and, below, the local Suspense fallback around
  // `{children}`. The same component staying on screen across
  // verifying-auth → page-chunk-loading turns three visually different
  // loading states into one continuous "shell up, content filling in"
  // transition. Auth-gate correctness (redirects, permission checks) is
  // untouched — only what renders *while* those checks are pending changes.
  if (!isInitialized || isLoading) {
    if (isAdminShellPath) {
      return <PageSkeleton />;
    }

    return <LoadingSpinner text={t('verifying_authentication')} />;
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // W1.2 — 2FA takes routing priority over EVERYTHING else, including setup:
  // the session is genuinely unusable server-side (EnsureTwoFactorVerified
  // returns 423 on setup/onboarding endpoints too) until the challenge or
  // mandatory enrollment is cleared.
  if (requiresTwoFactorEnrollment && location.pathname !== '/2fa/enroll') {
    return <Navigate to="/2fa/enroll" replace />;
  }
  if (requiresTwoFactor && location.pathname !== '/2fa/challenge') {
    return <Navigate to="/2fa/challenge" state={{ from: location }} replace />;
  }
  // A verified session has no business sitting on the challenge/enroll
  // screens — send it on to its normal destination.
  if (
    !requiresTwoFactor &&
    !requiresTwoFactorEnrollment &&
    (location.pathname === '/2fa/challenge' || location.pathname === '/2fa/enroll')
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  // Setup takes routing priority over onboarding.
  // Redirect to /setup if required (except when already on /setup)
  if (isSetupRequired && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }

  // #345: onboarding is OPT-IN once setup is complete — no hard redirect.
  // The OnboardingProgressBanner (AdminLayout) nudges the operator instead.
  // The previous hard gate trapped operators away from /dashboard and all
  // Settings pages until a first product AND customer existed.

  // Check permissions if specified
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/forbidden" replace />;
  }

  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return <Navigate to="/forbidden" replace />;
    }
  }

  // User is authenticated and has required permissions.
  // Wrap in a RouteErrorBoundary so render-phase errors in any admin page
  // surface as a clean in-app error state rather than a blank shell or a
  // browser chrome-error page (#535, #536, #545, #551).
  const content = <RouteErrorBoundary>{children}</RouteErrorBoundary>;

  if (!isAdminShellPath) {
    return content;
  }

  // #676 — catch the destination page's own lazy-chunk suspense HERE, with
  // the same `PageSkeleton` fallback the loading gate above already showed,
  // instead of letting it bubble past this component to the App-level
  // Suspense boundary in App.tsx (which would unmount this whole subtree and
  // swap in a second, differently-shaped skeleton — the "shell replace"
  // flash reported in #676).
  return <Suspense fallback={<PageSkeleton />}>{content}</Suspense>;
}
